import React, { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Bookmark, Trash2, Clock, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BookmarksPage = () => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [bookmarks, setBookmarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [removing, setRemoving] = useState(null);

  useEffect(() => {
    if (isAuthenticated) {
      fetchBookmarks();
    }
  }, [isAuthenticated]);

  const fetchBookmarks = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/bookmarks`, {
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        setBookmarks(data);
      } else {
        throw new Error('Failed to fetch bookmarks');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const removeBookmark = async (reviewId) => {
    setRemoving(reviewId);
    try {
      const response = await fetch(`${API_URL}/api/bookmarks/${reviewId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      
      if (response.ok) {
        setBookmarks(bookmarks.filter(b => b.review_id !== reviewId));
      }
    } catch (err) {
      console.error('Failed to remove bookmark:', err);
    } finally {
      setRemoving(null);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (authLoading) {
    return (
      <div className="bg-[#0B0F0E] min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-[#52ea88] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="bg-[#0B0F0E] min-h-screen">
      <Header />
      
      <main className="pt-32 pb-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-[7.6923%]">
          {/* Header */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <Bookmark className="w-8 h-8 text-[#52ea88]" />
              <h1 className="text-3xl md:text-4xl font-semibold text-[#E6FFF6]">
                Saved Reviews
              </h1>
            </div>
            <p className="text-[#9FBFB4] max-w-2xl">
              Your collection of bookmarked film analyses. Revisit your favorite reviews anytime.
            </p>
          </div>

          {/* Content */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-10 h-10 border-2 border-[#52ea88] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : error ? (
            <div className="text-center py-20">
              <p className="text-red-400 mb-4">{error}</p>
              <button
                onClick={fetchBookmarks}
                className="px-4 py-2 bg-[#52ea88] text-[#0B0F0E] font-medium"
              >
                Try Again
              </button>
            </div>
          ) : bookmarks.length === 0 ? (
            <div className="text-center py-20">
              <Bookmark className="w-16 h-16 text-[#52ea88]/30 mx-auto mb-4" />
              <h2 className="text-xl text-[#E6FFF6] mb-2">No saved reviews yet</h2>
              <p className="text-[#9FBFB4] mb-6">
                Start exploring and bookmark reviews you want to read later.
              </p>
              <Link
                to="/reviews"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#52ea88] text-[#0B0F0E] font-semibold hover:bg-[#52ea88]/90 transition-colors"
              >
                Browse Reviews
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {bookmarks.map((bookmark) => (
                <div
                  key={bookmark.id}
                  className="group bg-[#121917] border border-[#52ea88]/10 hover:border-[#52ea88]/30 transition-colors overflow-hidden"
                >
                  <Link to={`/review/${bookmark.review_id}`}>
                    {bookmark.review?.poster_path && (
                      <div className="aspect-[16/9] overflow-hidden">
                        <img
                          src={`https://image.tmdb.org/t/p/w500${bookmark.review.poster_path}`}
                          alt={bookmark.review.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    )}
                  </Link>
                  
                  <div className="p-5">
                    <Link to={`/review/${bookmark.review_id}`}>
                      <h3 className="text-lg font-semibold text-[#E6FFF6] group-hover:text-[#52ea88] transition-colors mb-2">
                        {bookmark.review?.title || 'Untitled Review'}
                      </h3>
                    </Link>
                    
                    <p className="text-sm text-[#9FBFB4] line-clamp-2 mb-4">
                      {bookmark.review?.excerpt}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-1 text-xs text-[#9FBFB4]/60">
                        <Clock className="w-3 h-3" />
                        Saved {formatDate(bookmark.created_at)}
                      </span>
                      
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          removeBookmark(bookmark.review_id);
                        }}
                        disabled={removing === bookmark.review_id}
                        className="p-2 text-[#9FBFB4] hover:text-red-400 hover:bg-red-400/10 transition-colors disabled:opacity-50"
                        title="Remove bookmark"
                      >
                        {removing === bookmark.review_id ? (
                          <div className="w-4 h-4 border border-current border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default BookmarksPage;
